import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { extractExitYear, matchDeterministicIntent } from '../lib/aiContext';

const DEFAULT_SUGGESTIONS = [
  'Net income if I sell in 4 years?',
  'How much cash until handover?',
  "What's my ROI at maturity?",
  'Rent vs mortgage-free cash flow per month?',
  'Break-even year?',
];

// Collapsible "Ask about this deal" chat, grounded in the CURRENT computed analysis.
// Deterministic questions (exit year, cash-to-handover, ROI at maturity, break-even,
// monthly net income) are answered locally straight from the engine's own numbers — no
// network call, and the figure can never drift from what the report shows. Anything
// else is sent to the AI backend along with the full structured context so it stays
// grounded in real numbers instead of guessing.
//
// Deliberately mounted OUTSIDE the PDF-captured ref by every caller — never appears in
// an exported PDF.
export default function AnalysisAssistant({
  context, contextVersion, askUrl, authHeaders, matchInput, onExitYearChange,
  suggestions = DEFAULT_SUGGESTIONS, title = 'Ask about this deal',
}) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // { role, text, version, exitYear, basedOn, error }
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open) scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading, open]);

  async function send(text) {
    const question = (text ?? input).trim();
    if (!question || loading || !context) return;
    setMessages(prev => [...prev, { role: 'user', text: question }]);
    setInput('');

    // Sync the Exit Scenario slider whenever the question names a holding period —
    // even if the question ends up going to the AI, not just on a deterministic hit.
    const maxN = (matchInput?.analysis?.exitScenarios || []).length - 1;
    const targetYear = extractExitYear(question, maxN);
    if (targetYear != null && onExitYearChange) {
      onExitYearChange(targetYear);
      document.getElementById('exit-scenario-card')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    const det = matchInput && matchDeterministicIntent(question, matchInput);
    if (det) {
      setMessages(prev => [...prev, { role: 'ai', text: det.answer, version: contextVersion, exitYear: det.exitYear, basedOn: det.basedOn }]);
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(askUrl, { question, context }, { headers: authHeaders, timeout: 600000 });
      setMessages(prev => [...prev, { role: 'ai', text: data.answer, version: contextVersion, basedOn: 'ai' }]);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Sorry, I could not get an answer right now. Please try again.';
      setMessages(prev => [...prev, { role: 'ai', text: `⚠️ ${msg}`, error: true }]);
    } finally {
      setLoading(false);
    }
  }

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div className="bg-white rounded-[1.75rem] border border-black/[0.06] shadow-premium mb-6 overflow-hidden">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-3.5 text-sm font-semibold text-gray-700 select-none">
        <span className="flex items-center gap-2">💬 {title} <span className="text-[11px] font-normal text-gray-400">— grounded in the numbers above, not a free-form guess</span></span>
        <span className={`text-gray-300 text-xs transition-transform ${open ? 'rotate-180' : ''}`}>▼</span>
      </button>
      {open && (
        <div className="border-t border-gray-50 px-5 pb-5 pt-4">
          {messages.length === 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {suggestions.map(s => (
                <button key={s} onClick={() => send(s)} disabled={!context}
                  className="text-left text-xs bg-gray-50 border border-gray-200 hover:border-green-400 hover:bg-green-50 rounded-xl px-3 py-2 text-gray-600 transition disabled:opacity-40">
                  {s}
                </button>
              ))}
            </div>
          )}

          {messages.length > 0 && (
            <div ref={scrollRef} className="space-y-3 max-h-80 overflow-y-auto mb-3 pr-1">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-[1.75rem] px-3.5 py-2 text-sm whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-green-600 text-white rounded-br-sm'
                      : m.error
                        ? 'bg-red-50 text-red-600 border border-red-100 rounded-bl-sm'
                        : 'bg-gray-50 text-gray-800 border border-black/[0.06] rounded-bl-sm'
                  }`}>
                    {m.text}
                    {m.role === 'ai' && !m.error && (
                      m.version !== contextVersion ? (
                        <p className="text-[10px] text-amber-600 mt-1.5 font-medium">⚠ assumptions have changed since this answer — ask again for a current figure</p>
                      ) : (
                        <p className="text-[10px] text-gray-400 mt-1.5">based on current assumptions{m.basedOn && m.basedOn !== 'ai' ? ' · exact figure from the model' : ''}</p>
                      )
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-gray-50 text-gray-500 border border-black/[0.06] rounded-[1.75rem] rounded-bl-sm px-3.5 py-2 text-sm flex items-center gap-2">
                    <span className="w-3 h-3 border-2 border-gray-300 border-t-green-600 rounded-full animate-spin"></span>
                    Thinking… (can take up to 30s)
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2 items-end">
            <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={onKeyDown} rows={1}
              placeholder="Ask about this deal's numbers…"
              className="flex-1 resize-none border border-gray-200 rounded-xl px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 max-h-28" />
            <button onClick={() => send()} disabled={loading || !input.trim() || !context}
              className="bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white px-4 py-2 rounded-xl text-sm font-medium transition">
              Ask
            </button>
          </div>
          {!context && <p className="text-[11px] text-gray-400 mt-2">Loading the analysis data…</p>}
        </div>
      )}
    </div>
  );
}
